import { Chhatri, Dome, TONES } from "./Architecture";
import { cuspedArch, pointedArch } from "./geometry";

const PANELS = [60, 240, 420, 600];
const STRAPS = [222, 402, 582];

const Rosette = ({ cx, cy }) => (
  <g transform={`translate(${cx} ${cy})`}>
    {Array.from({ length: 8 }, (_, k) => (
      <ellipse key={k} cx="0" cy="-15" rx="6" ry="14" transform={`rotate(${k * 45})`} fill="#c08a48" opacity="0.85" />
    ))}
    {Array.from({ length: 8 }, (_, k) => (
      <ellipse key={`i${k}`} cx="0" cy="-8" rx="3.4" ry="7" transform={`rotate(${k * 45 + 22.5})`} fill="#5c2b0e" opacity="0.7" />
    ))}
    <circle r="6.5" fill="url(#rm-brass)" />
  </g>
);

/** One carved teak door leaf with brass studs, straps and a ring knocker. */
export const DoorLeaf = ({ side = "left", className = "" }) => (
  <svg className={`rm-door__leaf-art ${className}`} viewBox="0 0 300 800" preserveAspectRatio="none" aria-hidden="true">
    <g transform={side === "right" ? "translate(300 0) scale(-1 1)" : undefined}>
      <rect width="300" height="800" fill="url(#rm-teak)" />
      <rect x="10" y="10" width="280" height="780" fill="none" stroke="#2e1204" strokeWidth="3" />
      <rect x="18" y="18" width="264" height="764" fill="none" stroke="#b07a3e" strokeWidth="1" opacity="0.5" />
      {PANELS.map((y) => (
        <g key={y}>
          <rect x="44" y={y} width="212" height="150" rx="4" fill="url(#rm-teak-panel)" />
          <path d={`M44 ${y + 150} L44 ${y} L256 ${y}`} fill="none" stroke="#c58a4a" strokeWidth="1.6" opacity="0.6" />
          <path d={`M256 ${y} L256 ${y + 150} L44 ${y + 150}`} fill="none" stroke="#230c02" strokeWidth="2" opacity="0.7" />
          <rect x="60" y={y + 16} width="180" height="118" rx="3" fill="none" stroke="#230c02" strokeWidth="1.2" opacity="0.4" />
          <Rosette cx={150} cy={y + 75} />
          {[
            [70, y + 26],
            [230, y + 26],
            [70, y + 124],
            [230, y + 124],
          ].map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.6" fill="url(#rm-brass)" />
          ))}
        </g>
      ))}
      {STRAPS.map((y) => (
        <g key={y}>
          <rect x="10" y={y} width="280" height="14" fill="url(#rm-gold-h)" opacity="0.92" />
          {Array.from({ length: 10 }, (_, i) => (
            <circle key={i} cx={24 + i * 28} cy={y + 7} r="3.4" fill="#6e4b1c" />
          ))}
        </g>
      ))}
      {Array.from({ length: 17 }, (_, i) => (
        <g key={`s${i}`}>
          <circle cx="28" cy={36 + i * 46} r="4.4" fill="url(#rm-brass)" />
          <circle cx="272" cy={36 + i * 46} r="4.4" fill="url(#rm-brass)" />
        </g>
      ))}
      <circle cx="262" cy="412" r="11" fill="url(#rm-brass)" />
      <circle cx="262" cy="440" r="24" fill="none" stroke="url(#rm-gold)" strokeWidth="5" />
      <rect x="294" y="0" width="6" height="800" fill="#1e0a02" opacity="0.6" />
    </g>
  </svg>
);

const W = 760;
const TOP = -190;
const H = 1000;

// Where the door opening sits inside the gateway art, as fractions of its box
// (used by the CSS to place the light and the leaves behind the frame).
export const DOOR_OPENING = { x: 130, y: 170, w: 500, h: 830 };
export const GATE_VIEWBOX = { x: 0, y: TOP, w: W, h: H - TOP };

const opening = cuspedArch({ ...DOOR_OPENING, spring: 230, lobes: 5 });

const Pilaster = ({ x }) => (
  <g>
    <rect x={x} y="290" width="74" height={H - 290} fill="url(#rm-stone)" />
    <rect x={x + 56} y="290" width="18" height={H - 290} fill="url(#rm-stone-shade)" />
    <rect x={x - 6} y="282" width="86" height="12" fill="url(#rm-dome)" />
    {Array.from({ length: 7 }, (_, i) => (
      <path
        key={i}
        d={pointedArch({ x: x + 13, y: 316 + i * 94, w: 48, h: 70, spring: 26 })}
        fill="#7d5129"
        opacity="0.45"
      />
    ))}
  </g>
);

/** Sandstone palace gateway with a domed crown and a cusped arch opening; sits on top of the leaves. */
export const DoorFrame = ({ className = "" }) => {
  const t = TONES.day;
  return (
    <svg
      className={`rm-door__frame-art ${className}`}
      viewBox={`${GATE_VIEWBOX.x} ${GATE_VIEWBOX.y} ${GATE_VIEWBOX.w} ${GATE_VIEWBOX.h}`}
      preserveAspectRatio="xMidYMax meet"
      aria-hidden="true"
    >
      <g filter="url(#rm-paint-soft)">
        <Chhatri cx={78} by={30} w={112} t={t} ribs={8} />
        <Chhatri cx={W - 78} by={30} w={112} t={t} ribs={8} />
        <rect x="296" y="-36" width="168" height="66" fill="url(#rm-stone)" />
        <Dome cx={380} by={-36} r={70} t={t} ribs={12} />

        <path d={`M0 30 H${W} V${H} H0 Z ${opening.d} Z`} fillRule="evenodd" fill="url(#rm-stone)" />
        <path d={`M${W * 0.84} 30 H${W} V${H} H${W * 0.84} Z`} fill="url(#rm-stone-shade)" opacity="0.55" />
        <Pilaster x={34} />
        <Pilaster x={W - 108} />
        {[150, W - 150].map((cx) => (
          <g key={cx} transform={`translate(${cx} 172)`}>
            <circle r="34" fill="none" stroke="#8a5f33" strokeWidth="1.4" opacity="0.6" />
            {Array.from({ length: 12 }, (_, k) => (
              <ellipse key={k} cx="0" cy="-18" rx="5" ry="12" transform={`rotate(${k * 30})`} fill="#a97a45" opacity="0.6" />
            ))}
            <circle r="7" fill="url(#rm-brass)" />
          </g>
        ))}
        <rect x="0" y="30" width={W} height="36" fill="url(#rm-stone)" />
        <path d={`M-12 66 H${W + 12} L${W - 8} 98 H8 Z`} fill="url(#rm-dome)" />
        <line x1="0" y1="98" x2={W} y2="98" stroke="#6c3f17" strokeWidth="1.5" opacity="0.5" />
        <rect x="0" y="98" width={W} height="32" fill="url(#rm-jaali)" opacity="0.85" />
        {Array.from({ length: Math.floor(W / 24) }, (_, i) => (
          <path
            key={i}
            d={`M${i * 24 + 6} 30 L${i * 24 + 6} 20 Q${i * 24 + 12} 12 ${i * 24 + 18} 20 L${i * 24 + 18} 30 Z`}
            fill="url(#rm-stone)"
          />
        ))}
        <rect x="0" y={H - 30} width={W} height="30" fill="#b48448" />
      </g>
      <path d={opening.d} fill="none" stroke="url(#rm-gold)" strokeWidth="7" />
      <path d={opening.d} fill="none" stroke="#f3dca8" strokeWidth="1.2" opacity="0.8" />
    </svg>
  );
};
